import { useMutation, useQueryClient, type UseMutationOptions } from '@tanstack/react-query';
import { useApiClient } from './useApiClient.ts';
import type { ApiError } from './useApi.ts';
import { apiQueryKey } from './queryKeys.ts';
import { useTenantContext } from './contexts.tsx';
export { normalizeBatchResult } from './batchResult.ts';
export type { BatchResult, BatchItemResult, BatchEndpointResponse } from './batchResult.ts';

/**
 * useOptimisticMutation — optimistic single-write support for the assembly
 * flows where operators chain several writes and should not see a spinner
 * between each step (A5-194).
 *
 * `useApiMutation` only does single, fire-and-wait writes; the cache only
 * updates after the server round-trips, so a 4-step assembly shows 4 spinners.
 * {@link useOptimisticMutation} applies the expected result to the cache in
 * `onMutate`, captures a rollback snapshot, reverts it on error — the UI
 * reflects the write instantly and self-heals if the server rejects it.
 *
 * `normalizeBatchResult` (re-exported above) remains available for callers that
 * assemble their own batch mutations from a `/<endpoint>/batch` response.
 */

// ── Optimistic single mutation ──────────────────────────────────────────────

export interface OptimisticMutationConfig<TData, TVariables> {
  /** Resource cache key affected by the write, e.g. `'/equipment/lathe-1'`. */
  queryKey: string;
  /** HTTP method (default POST). */
  method?: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /**
   * Produce the optimistic next cache value from the previous value + the
   * mutation variables. Returns the value to write into the cache immediately.
   */
  optimisticUpdate: (previous: TData | undefined, variables: TVariables) => TData;
  /** TanStack callbacks the caller wants to add (merged after rollback wiring). */
  options?: Omit<
    UseMutationOptions<TData, ApiError, TVariables, OptimisticContext<TData>>,
    'mutationFn' | 'onMutate' | 'onError' | 'onSettled'
  >;
}

/** Rollback snapshot captured in `onMutate` and restored in `onError`. */
export interface OptimisticContext<TData> {
  previous: TData | undefined;
  resolvedKey: ReturnType<typeof apiQueryKey>;
}

/**
 * A mutation that writes its expected result into the cache immediately
 * (`onMutate`), records a rollback snapshot, reverts on error, and reconciles
 * with the server on settle.
 */
export function useOptimisticMutation<TData = unknown, TVariables = unknown>(
  config: OptimisticMutationConfig<TData, TVariables>,
) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const { tenantId } = useTenantContext();
  const method = config.method ?? 'POST';

  return useMutation<TData, ApiError, TVariables, OptimisticContext<TData>>({
    mutationFn: (variables: TVariables) =>
      client.request<TData>(config.queryKey, {
        method,
        json: method === 'DELETE' ? undefined : variables,
      }),
    onMutate: async (variables) => {
      const resolvedKey = apiQueryKey(config.queryKey, tenantId);
      // Cancel in-flight refetches so they don't overwrite the optimistic value.
      await queryClient.cancelQueries({ queryKey: resolvedKey });
      const previous = queryClient.getQueryData<TData>(resolvedKey);
      queryClient.setQueryData<TData>(resolvedKey, config.optimisticUpdate(previous, variables));
      return { previous, resolvedKey };
    },
    onError: (_err, _variables, context) => {
      // Revert to the pre-mutation snapshot.
      if (context) {
        queryClient.setQueryData(context.resolvedKey, context.previous);
      }
    },
    onSettled: (_data, _err, _variables, context) => {
      // Reconcile with the server regardless of outcome.
      if (context) {
        queryClient.invalidateQueries({ queryKey: context.resolvedKey });
      }
    },
    ...config.options,
  });
}
